/**
 * The static addresses the application links back to, and the words on them
 * (flow ticket 02, and `/privacy` in ticket 09).
 *
 * The green room's header carries the two things `/`, `/how-it-works` and
 * `/privacy` grew in ticket 01 — the mark home, and one link to the walkthrough —
 * and the `?` in the corner of the prompter and the settings screen is the second
 * of those links wearing a circle — {@link HowItWorksButton}, one component drawn by
 * both. The fourth anchor is the room's own, under its script box: the sentence
 * about what happens to your video, and the link to the whole of it. Four anchors
 * across three screens, naming three addresses.
 *
 * **A module rather than the literals spread across them**, which is the whole
 * reason this file exists. `page/` may not import `scripts/`
 * (`ships-to-the-browser.test.ts`), so `PAGES` is out of reach and the addresses
 * have to be written out somewhere inside the bundle; the choice is between
 * writing them once and writing them on each screen. `site-links.test.ts` is the
 * other end of the rope and holds every constant here to the record that decides
 * it — the same arrangement `link-through.ts` and `APP_PATH` are already on, and
 * since ticket 09 it checks that there is a row for each rather than trusting
 * whoever adds the next one to write it.
 *
 * **The words are copied out of `site-copy.ts` and `landing-copy.ts` rather than
 * imported from them**, and that is a measurement rather than a preference: those
 * files are the landing page's prose, and `landing-page.test.ts` asserts none of
 * it reaches `main.js`. A `page/` module importing them would put the headline,
 * the pitch, the FAQ and the four privacy claims in the application's bundle to
 * save writing a few sentences down. The rows in `site-links.test.ts` are what
 * keep the two copies the same words.
 */

/**
 * Home, which is the site rather than the application.
 *
 * `PAGES.landing.canonical`. The mark in the room's header goes here, so somebody
 * who followed the front page's call to action can get back to what convinced
 * them — which they could not, for the four days between the seo effort
 * separating the two and this ticket joining them up.
 */
export const HOME = "/";

/**
 * The walkthrough, at the address a search result leads to.
 *
 * `PAGES["how-it-works"].canonical`. **One rendering of `WALKTHROUGH_STEPS` for a
 * reader, not two.** The in-app `HelpPage` was the other, and it is deleted with
 * this constant's arrival: the same seven steps, at an address, indexed, and
 * shareable.
 */
export const HOW_IT_WORKS = "/how-it-works";

/**
 * The mark, which is the product's name.
 *
 * `site-copy.ts`' `SITE_MARK`, which is in turn `head.ts`' `SITE_NAME`. The room
 * has named itself with this word since web-ui ticket 05; what changes here is
 * only that the word is now a link.
 */
export const SITE_MARK = "cuekards";

/**
 * The mark's lettering — the wordmark the three static pages carry, copied here for
 * the reason {@link SITE_MARK} is: `scripts/logo.ts` decides the served path and the
 * box, and `page/` may not import `scripts/` (`ships-to-the-browser.test.ts`).
 * `LOGO_PATH` and `LOGO_BOX` are what these two constants repeat.
 */
export const MARK_IMAGE = "/logo/cuekards-wordmark.png";

/** {@link LOGO_BOX}, copied for the reason {@link MARK_IMAGE} is. */
export const MARK_IMAGE_BOX = { width: 101, height: 28 } as const;

/** The card drawn beside the lettering — `scripts/mark.ts`' favicon, which is also
 * the phone's launcher icon. `FAVICON_SVG`, copied for the reason {@link MARK_IMAGE}
 * is. */
export const MARK_ICON = "/favicon.svg";

/** `logo.ts`' `MARK_ICON_BOX`, copied for the same reason. */
export const MARK_ICON_BOX = { width: 24, height: 24 } as const;

/**
 * What the one link in the room's bar says.
 *
 * `site-copy.ts`' `TOP_NAV_LABEL`, so this link and the first of the documents'
 * three read identically — the sameness is what a bar buys, and a room wording the
 * same address differently is a room a reader has to re-read. It is the *link* that
 * has to match rather than the bar: signal-and-paper ticket 06 gave the documents
 * two more of them and a button, none of which is anything to offer somebody who is
 * already inside the application.
 */
export const TOP_NAV_LABEL = "How it works";

/**
 * The whole privacy claim, at an address (flow ticket 09).
 *
 * `PAGES.privacy.canonical`. The third static address, and the one the
 * application had no link to at all between flow ticket 02 and this: `HelpPage`
 * ended on {@link PRIVACY_SHORT} and a link to here, the screen was deleted, and
 * nothing carried either anywhere else — so for the length of that gap no `.tsx`
 * in the product said a word about what it does with your video. ADR-0026 records
 * it as a gap rather than as an arrangement, and this constant is the fix.
 */
export const PRIVACY = "/privacy";

/**
 * What happens to your video, in one sentence.
 *
 * **The room says this in its own words, and that is deliberate.** The rest of this
 * module is a held copy of what the documents say, because a bar that worded an
 * address differently is a bar a reader has to re-read. This is not: `/how-it-works`
 * ends on `landing-copy.ts`' `PRIVACY_SHORT`, the technical statement — the tab, the
 * three kinds of data — written for somebody who has chosen to read about the
 * product. Here the reader is one press from a take, looking at a preview of their
 * own face, and a sentence long enough to be read properly is a sentence nobody
 * reads at all.
 *
 * So the words are shorter and are this module's own. **The claim underneath is
 * not**: `landing-copy.test.ts` reads this constant beside the other two short forms
 * and holds all three to `PRIVACY_CLAIMS`, so the room may be briefer than the
 * document and may not promise more than it. `site-links.test.ts` names it as
 * deliberately its own, and fails if it ever comes back into line without the row
 * moving with it.
 *
 * It goes under the room's script box rather than in the room's bar. ADR-0026 made
 * that call on the bars being word for word, which they stopped being when
 * signal-and-paper ticket 06 gave the documents a link to `/privacy` of their own;
 * what survives the change is the reason underneath it — the room's header is the
 * two things a reader has already met at the head of every document, and a header
 * that grows a link per ticket is a header nobody reads. The box is where somebody
 * is about to paste a script in front of a camera preview of their own face, which
 * is the moment reassurance is worth anything.
 */
export const PRIVACY_SHORT =
  "All recordings are saved to your device only.";

/**
 * What the link to the whole claim says.
 *
 * `site-copy.ts`' `LANDING_LABELS.privacyMore`, copied on the same argument as
 * {@link PRIVACY_SHORT}. It is already the name of this one action on `/` and on
 * `/how-it-works` — `documents.ts` renders the same pair on both — so the room
 * gets the same action on a fourth surface rather than a fourth wording of it.
 */
export const PRIVACY_MORE = "Privacy policy";

/**
 * How every one of these links opens, as one object spread onto every anchor.
 *
 * **This is what
 * [ADR-0025](../../docs/adr/0025-the-site-and-the-app-are-different-pages.md)
 * has to be amended about**, and flow ticket 08 is where that amendment is
 * written — the ADR itself still reads as it did, so do not take this comment for
 * it. That decision kept the in-app help a screen rather than a link, because "a
 * navigation away from a tab that is recording destroys the take", and the `?` is
 * live on the prompter *during* a take. The reasoning is correct; this is the
 * answer to it. `target="_blank"` is not a navigation away from the tab: the take
 * keeps rolling, the camera stays open, and the reader gets the page a search
 * result would have given them.
 *
 * So it is one constant rather than two attributes retyped on every anchor. An
 * anchor that lost its `target` would be invisible in a diff and would end a
 * take, which is too sharp an edge to leave to copy-and-paste.
 *
 * `rel="noopener"` beside it because a `_blank` without one hands the opened
 * document a live `window.opener` back into this tab.
 */
export const NEW_TAB = { target: "_blank", rel: "noopener" } as const;

/**
 * The `?` in the corner of the prompter and the settings screen, as one
 * component (flow ticket 02).
 *
 * It is `/how-it-works` wearing a circle, and it is a
 * component rather than markup on each screen for the reason {@link NEW_TAB} is a
 * constant: five attributes have to agree across both, one of them is what keeps
 * the link from ending a take, and two of them are words. A second copy of this
 * anchor is a second place for any of the five to drift.
 *
 * **The green room does not use it.** Its version of the same link is a word
 * rather than a glyph — the room has the width, and the documents' own bar says
 * the words in the same place — so what the two share is the address and the
 * label, which is exactly what this module holds.
 *
 * `title` and `aria-label` are both {@link TOP_NAV_LABEL}: a `?` says nothing to
 * a screen reader and nothing on hover, and the words it stands for are the same
 * words the room and the three documents use for the same address.
 */
export function HowItWorksButton(): JSX.Element {
  return (
    <a
      className="web-head-button web-how-link"
      href={HOW_IT_WORKS}
      {...NEW_TAB}
      title={TOP_NAV_LABEL}
      aria-label={TOP_NAV_LABEL}
    >
      ?
    </a>
  );
}
